import { useApolloClient, useSuspenseQuery } from '@apollo/client';
import React, { Suspense, useCallback } from 'react';

import { StartingChatMessage } from '@eluve/blocks';
import {
  Button,
  DetailsSidebar,
  DetailsSidebarProvider,
  DetailsSidebarTrigger,
  Icon,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { ChatComponent } from './ChatComponent';
import { ChatPanelSkeleton } from './ChatPanelSkeleton';
import { getChatConversationStartersQuery } from './chat.operations';
import {
  mergeStarters,
  useResolvedConversationStarters,
} from './useResolvedConversationStarters';

interface ChatWithPatientDataProps {
  tenantId: string;
  patientId: string;
  baseUrl: string;
  appointmentId?: string;
}

interface ChatWithPatientDataComponentProps extends ChatWithPatientDataProps {
  /** Element that opens the chat pane. Defaults to the icon-only button. */
  trigger?: React.ReactNode;
}

const ChatWithPatientDataContent: React.FC<ChatWithPatientDataProps> = ({
  tenantId,
  patientId,
  baseUrl,
  appointmentId,
}) => {
  const userId = useUserIdFromSession();
  const { capture } = useAnalytics();
  const { data, refetch } = useSuspenseQuery(getChatConversationStartersQuery, {
    variables: { tenantId, userId },
  });

  const { starters: resolvedDefaults } =
    useResolvedConversationStarters(tenantId);
  const userCustomStarters = data.tenantUserChatSettingsByPk
    ?.conversationStarters as StartingChatMessage[] | undefined;
  const conversationStarters = mergeStarters(
    userCustomStarters,
    resolvedDefaults,
  );

  const apolloClient = useApolloClient();
  const handleNoteUpdated = useCallback(() => {
    apolloClient.refetchQueries({
      include: ['getCurrentSummary', 'getCurrentSummaryForEluveAdmin'],
    });
  }, [apolloClient]);

  const handleCustomInstructionSaved = useCallback(() => {
    apolloClient.refetchQueries({
      include: ['getTenantUserSettings'],
    });
  }, [apolloClient]);

  const handleDocumentAdded = useCallback(
    (doc?: { id: string; title: string }) => {
      if (doc?.id) {
        capture('document_generated', {
          document_id: doc.id,
          origin: 'chat',
          tenant_id: tenantId,
          patient_id: patientId,
          appointment_id: appointmentId,
        });
      }
      apolloClient.refetchQueries({
        include: ['patientArtifacts', 'getDocumentReferencesByAppointment'],
      });
    },
    [apolloClient, capture, tenantId, patientId, appointmentId],
  );

  return (
    <ChatComponent
      tenantId={tenantId}
      baseUrl={baseUrl}
      config={{
        patientId,
        appointmentId,
        startingMessages: conversationStarters,
      }}
      onStartersChanged={() => refetch()}
      onNoteUpdated={appointmentId ? handleNoteUpdated : undefined}
      onCustomInstructionSaved={handleCustomInstructionSaved}
      onDocumentAdded={handleDocumentAdded}
    />
  );
};

export const ChatWithPatientData: React.FC<
  ChatWithPatientDataComponentProps
> = ({ trigger, ...props }) => {
  const { t } = useTranslation();
  const { capture } = useAnalytics('chat');

  return (
    <DetailsSidebarProvider
      overlayOnly
      onOpenChange={(open) =>
        capture(open ? 'chat_pane_opened' : 'chat_pane_closed')
      }
      onFullscreenChange={(expanded) =>
        capture('chat_fullscreen_toggled', { expanded })
      }
    >
      <DetailsSidebarTrigger>
        {trigger ?? (
          <Button
            variant="outline"
            aria-label={t('chat.header.patientInsights')}
          >
            <Icon name="MessageSquare" size="auto" />
          </Button>
        )}
      </DetailsSidebarTrigger>
      <DetailsSidebar
        size="lg"
        title={t('chat.header.patientInsights')}
        allowFullscreen
      >
        <Suspense fallback={<ChatPanelSkeleton />}>
          <ChatWithPatientDataContent {...props} />
        </Suspense>
      </DetailsSidebar>
    </DetailsSidebarProvider>
  );
};
